// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// main.ts — mount, screens, input, and the wiring between them.
//
// Three things in here are load-bearing rather than incidental:
//
//  1. THE GRID IS DOM, THE PAINT IS CANVAS. Hit-testing is native (no stale rects, no coordinate
//     scaling), and the fills are a real composite the contrast probe can read.
//  2. NO VERB EVER SHARES A SURFACE WITH THE DRAG. The board does exactly two things — drag routes,
//     tap selects — and all four verbs are hold-buttons in the bottom bar. That deletes the whole
//     class of "the 8px slop turned my hold into a redraw" misfires, and gives the non-drag path
//     WCAG 2.5.7 wants for free.
//  3. ONE ROOM PER SESSION. The Net is created once when a room is entered and lives until the
//     player goes back to the menu; every rematch is a new round INSIDE it.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { createNet, roomAppId, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { setTurnConfig } from '@ben-gy/game-engine/net';
import { createRounds, type RoundInfo, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  getOrCreateRoomCode,
  mintCode,
  normalizeRoomCode,
  setRoomInUrl,
  type Lobby,
} from '@ben-gy/game-engine/lobby';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { hardenViewport } from '@ben-gy/game-engine/mobile';

import { Game, type Ev } from './game';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { T } from './tuning';
import { CUE, PATCHES } from './cues';
import { PALETTE } from './palette';
import { createRenderer, type Rect } from './render';
import { cellAt, pathFromPointerSamples, type Geom, type Sample } from './path';
import { STEPS, allowedIn } from './tutorial';
import { createSession, type Session } from './net-game';

const SLUG = 'overpressure';
const VERB_NAMES = ['ROUTE', 'SEAL', 'DOUSE', 'VENT', 'FORCE'];

const store = createStore(SLUG);
const sfx = createSfx({ muted: store.get('muted', false), patches: PATCHES });
// A cue name that does not exist plays SILENTLY. Assert the table at boot so a typo is loud here
// instead of shipping a game with no sound and a green test suite.
for (const name of Object.values(CUE)) {
  if (!sfx.has(name)) throw new Error(`unknown sound cue: ${name}`);
}

const app = document.getElementById('app') as HTMLElement;
hardenViewport();

// ── shared state ────────────────────────────────────────────────────────────

let net: Net | null = null;
let rounds: Rounds<{ mode: string }> | null = null;
let lobby: Lobby | null = null;
let session: Session | null = null;
let game: Game | null = null;
let modeId = String(store.get('mode', 'patrol'));
let deepLinkUsed = false;
let matchTally = { runs: 0, best: 0, wins: 0 };

const el = (tag: string, cls?: string, html?: string): HTMLElement => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (html != null) n.innerHTML = html;
  return n;
};

const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function footer(): HTMLElement {
  return el(
    'footer',
    'site-footer',
    'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
      '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>',
  );
}

function mount(...nodes: HTMLElement[]): HTMLElement {
  app.textContent = '';
  const main = el('div', 'main-content');
  for (const n of nodes) main.appendChild(n);
  app.appendChild(main);
  app.appendChild(footer());
  return main;
}

// ── overlays: the dismissal contract ────────────────────────────────────────
//
// FOUR WAYS OUT, ALWAYS, not a choice of them: a >=44px close control, a tap anywhere on the
// backdrop, Escape, and a backdrop that is genuinely reachable. A sibling site shipped an overlay
// whose close button, backdrop handler and Escape all fired correctly and which was still impossible
// to dismiss, because the [hidden] guard was missing from its stylesheet.

function openSheet(title: string, body: string, onClose?: () => void): void {
  const overlay = el('div', 'overlay');
  const sheet = el('div', 'sheet', `<h2>${esc(title)}</h2>${body}`);
  const x = el('button', 'x', '&#10005;') as HTMLButtonElement;
  x.setAttribute('aria-label', 'Close');
  sheet.appendChild(x);
  overlay.appendChild(sheet);
  document.body.appendChild(overlay);

  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
    onClose?.();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  x.addEventListener('click', close);
  // pointerdown covers mouse, touch and pen in one handler. Never both touchstart AND click on the
  // same control — one tap fires it twice, which reads as a dead button.
  overlay.addEventListener('pointerdown', (e) => {
    if (!sheet.contains(e.target as Node)) close();
  });
  document.addEventListener('keydown', onKey);
  x.focus();
}

const HOW_TO_PLAY = `
<p>Drag from the <b>reactor</b> at the bottom, up through compartments, to route power. Cells past
your budget grey out — <b>the ship only runs what the cable reaches</b>.</p>
<p>Tap a compartment, then hold <b>SEAL</b> on a breach, <b>DOUSE</b> a fire, or <b>VENT</b> it —
venting is instant but it scars the bulkhead forever. Hold <b>FORCE</b> for more power and a lot
more heat.</p>
<p>Every action charges the core, and <b>four actions in two and a half seconds trips the bus and
drops the whole ship dark</b> — so space them out, and say what you're about to do.</p>
<ul>
  <li><b>Water kills fire.</b> Letting a compartment flood is a real way to put one out.</li>
  <li><b>But a wet compartment you are powering can short</b> — which starts a fire and cuts your
      line. Pumping the water out is what sets the room alight.</li>
</ul>
<p style="margin-top:12px">Desktop: <b>1-9 / Q-Y</b> select, <b>arrows</b> move, <b>A</b> append to
the cable, <b>Z</b> undo one, <b>R</b> reset, <b>S/D/V</b> seal / douse / vent, <b>F</b> force,
<b>Esc</b> pause.</p>`;

const ABOUT = `
<p>Overpressure is a co-op ship-survival game: route a reactor's power through a failing hull while
breaches, fires and floods feed each other. Built by
<a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.</p>
<p>Playing with friends is <b>peer-to-peer</b>. There is no game server and no account: a public
signalling relay only brokers the first connection between browsers, and no game state is ever
stored anywhere. Connecting over WebRTC exposes your IP address to the people you are playing
with — in a private room those are the people you invited.</p>
<p>No cookies, no fingerprinting, no third-party fonts. Anonymous, cookie-less page-view counts via
Cloudflare Web Analytics.</p>`;

// ── the menu ────────────────────────────────────────────────────────────────

function showMenu(): void {
  setPlaying(false);
  teardownRoom();
  const s = el('section', 'screen menu');
  s.appendChild(el('h1', undefined, 'Overpressure'));
  s.appendChild(
    el('p', 'tag', 'Seven power, twelve compartments, one thumb — draw the line and choose what burns.'),
  );

  const modes = el('div', 'modes');
  for (const id of MODE_IDS) {
    const m = modeOf(id);
    const b = el('button', 'mode-btn', `<b>${esc(m.name)}</b><span>${esc(m.blurb)}</span>`) as HTMLButtonElement;
    b.setAttribute('aria-pressed', String(id === modeId));
    b.addEventListener('click', () => {
      modeId = id;
      store.set('mode', id);
      sfx.unlock();
      sfx.play(CUE.tap);
      showMenu();
    });
    modes.appendChild(b);
  }
  s.appendChild(modes);

  const best = Number(store.get(`best:${modeId}`, 0));
  const play = el(
    'button',
    'btn primary',
    `Play solo<span class="sub">${modeOf(modeId).name}${best ? ` · best ${fmtScore(modeOf(modeId), best)}` : ''}</span>`,
  ) as HTMLButtonElement;
  play.addEventListener('click', () => {
    sfx.unlock();
    startSolo();
  });
  s.appendChild(play);

  const friends = el(
    'button',
    'btn',
    'Play with friends<span class="sub">Up to four of you on one ship</span>',
  ) as HTMLButtonElement;
  friends.addEventListener('click', () => {
    sfx.unlock();
    showRoomEntry();
  });
  s.appendChild(friends);

  const row = el('div', 'row');
  const how = el('button', 'btn ghost', 'How to play') as HTMLButtonElement;
  how.addEventListener('click', () => startTutorial());
  const about = el('button', 'btn ghost', 'About') as HTMLButtonElement;
  about.addEventListener('click', () => openSheet('About Overpressure', ABOUT));
  row.appendChild(how);
  row.appendChild(about);
  s.appendChild(row);

  const mute = el('button', 'btn ghost', sfx.muted() ? 'Sound: off' : 'Sound: on') as HTMLButtonElement;
  mute.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });
  s.appendChild(mute);

  mount(s);
}

const fmtScore = (m: Mode, v: number): string =>
  m.transitRate > 0 ? `${Math.round(v)}% across` : `${Math.round(v)}s held`;

// ── rooms ───────────────────────────────────────────────────────────────────

function teardownRoom(): void {
  session?.destroy();
  session = null;
  rounds?.destroy();
  rounds = null;
  lobby?.destroy();
  lobby = null;
  const n = net;
  net = null;
  if (n) void n.leave();
  clearRoomInUrl();
}

function showRoomEntry(): void {
  setPlaying(false);
  const s = el('section', 'screen');
  mount(s);
  createRoomEntry({
    container: s,
    title: 'Play with friends',
    subtitle: 'Create a room and share the code, or type in a code somebody gave you.',
    onSubmit: (code, created) => void enterRoom(normalizeRoomCode(code) || mintCode(), created),
    onCancel: () => showMenu(),
  });
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  // ONCE at boot, before ANY mesh. Trystero builds one global connection pool from the first
  // joinRoom's config; a later call leaves the initiating half of every pair STUN-only.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }
  setRoomInUrl(code);
  net = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelf) => {
        session?.setHost(isSelf);
        lobby?.repaint();
      },
      onPeerLeave: () => {
        session?.onRoster();
        lobby?.repaint();
      },
      onPeers: () => {
        session?.onRoster();
        lobby?.repaint();
      },
    },
  );

  rounds = createRounds<{ mode: string }>({
    net,
    playerName: playerName(),
    // ONE. A co-op vessel starts with whoever is present, and a lone survivor must be able to run a
    // rematch — minPlayers 2 is a versus-shape default and a hard deadlock here.
    minPlayers: 1,
    roundOpts: () => ({ mode: modeId }),
    onRound: (info) => startRound(info),
    onChange: () => {
      // THE HOST JOINS A ROUND THAT IS FORMING — it does not start one by existing.
      //
      // The engine lobby gives the host a Start button instead of a ready toggle, so the host never
      // casts a vote of its own. With `minPlayers: 1` that produced two opposite failures on
      // production, one after the other, and only the two-peer smoke test could see either:
      //
      //   1. Host does not vote: a single guest tapping ready reached quorum on its own and the
      //      round began WITHOUT the host, who was dropped onto the "round in progress, you're in
      //      the next one" spectator screen. Ejected from her own ship.
      //   2. Host always votes: the host's standing vote WAS quorum, so the room auto-started a solo
      //      round seconds after it was created — before anyone could possibly join. The guest was
      //      then left in a lobby saying "waiting for the host to start", for a round already
      //      running without them.
      //
      // Voting only once somebody ELSE has is the condition that satisfies both: a lone host waits
      // as long as they like, a guest readying up seats them both, and the quorum countdown is
      // untouched, so one player reading the summary still cannot hold the room.
      const st = rounds?.state();
      const someoneElseReady = !!st?.votes.some((v) => v.id !== net?.selfId);
      if (st && st.isHost && !st.voted && st.phase === 'waiting' && someoneElseReady) rounds?.vote();
      lobby?.repaint();
    },
  });

  showLobby(code);
}

function playerName(): string {
  const saved = String(store.get('name', ''));
  if (saved) return saved;
  const n = `Crew ${Math.floor(Date.now() % 97) + 1}`;
  store.set('name', n);
  return n;
}

function showLobby(code: string): void {
  setPlaying(false);
  const s = el('section', 'screen');
  mount(s);
  if (!net || !rounds) return showMenu();
  lobby = createLobby({
    container: s,
    net,
    rounds: rounds as unknown as Rounds,
    roomCode: code,
    minPlayers: 1,
    maxPlayers: 4,
    share: {
      title: 'Join my Overpressure crew',
      text: (c) => `Room ${c} — help me keep this ship alive`,
    },
    labels: { start: 'Take her out', cancel: 'Back to menu' },
    modeSlot: () => {
      const host = rounds?.state().hostOpts?.mode;
      const m = modeOf(host ?? modeId);
      // The HOST's pick is what the room plays. Rendering our own and labelling it the host's is a
      // confident lie, and it has shipped before.
      return `<p class="lobby-note">Ship: <b>${esc(m.name)}</b> — ${esc(m.blurb)}</p>`;
    },
    onCancel: () => showMenu(),
  });
}

// ── starting a run ──────────────────────────────────────────────────────────

function startSolo(): void {
  const seed = (Date.now() ^ (Math.random() * 0x7fffffff)) | 0;
  game = new Game({ seed, mode: modeOf(modeId), seats: 1 });
  session = createSession({ game, me: 0, ids: [null], onEnd: () => showResults() });
  showPlay({ counted: true });
}

function startRound(info: RoundInfo<{ mode: string }>): void {
  if (!net) return;
  const hostMode = info.opts?.mode ?? modeId;
  if (!info.seated) {
    // A peer that connected mid-round is not in the frozen roster. A spectator view of the real
    // ship, never a dead screen.
    showSpectator();
    return;
  }
  const ids = info.players.map((p) => p.id);
  const me = ids.indexOf(net.selfId);
  game = new Game({ seed: info.seed, mode: modeOf(hostMode), seats: info.players.length });
  session = createSession({
    game,
    me,
    ids,
    net,
    host: info.isHost,
    onEnd: () => showResults(info),
    onHostChange: () => hudRefresh?.(),
    onLink: (state) => linkRefresh?.(state),
  });
  showPlay({ counted: false, info });
}

function showSpectator(): void {
  setPlaying(false);
  const s = el('section', 'screen menu');
  s.appendChild(el('h2', undefined, 'Round in progress'));
  s.appendChild(el('p', 'tag', "The crew is already out. You're in the next one."));
  const b = el('button', 'btn ghost', 'Back to the lobby') as HTMLButtonElement;
  b.addEventListener('click', () => showLobby(getOrCreateRoomCode()));
  s.appendChild(b);
  mount(s);
}

// ── the play screen ─────────────────────────────────────────────────────────

interface PlayOpts {
  counted: boolean;
  info?: RoundInfo<{ mode: string }>;
  tutorial?: boolean;
}

let stopLoop: (() => void) | null = null;
/**
 * Hooks the live play screen publishes so the net handlers — which are created BEFORE it exists —
 * can repaint without reaching into its closure.
 */
let hudRefresh: (() => void) | null = null;
let linkRefresh: ((s: 'ok' | 'lost' | 'promoting') => void) | null = null;

function showPlay(opts: PlayOpts): void {
  if (!game) return showMenu();
  const g: Game = game;
  setPlaying(true);

  const screen = el('section', 'screen play');
  const stage = el('div', 'stage');
  const hud = buildHud();
  const wrap = el('div', 'board-wrap');
  const canvas = document.createElement('canvas');
  canvas.className = 'paint';
  const board = el('div', 'board');
  board.style.setProperty('--cols', String(g.mode.cols));
  board.style.setProperty('--rows', String(g.mode.rows));
  wrap.appendChild(canvas);
  wrap.appendChild(board);
  stage.appendChild(hud.root);
  stage.appendChild(wrap);
  const bar = buildBar();
  screen.appendChild(stage);
  screen.appendChild(bar.root);
  mount(screen);

  const cellBtns: HTMLButtonElement[] = [];
  for (let i = 0; i < g.n; i++) {
    const b = el('button', 'cellbtn') as HTMLButtonElement;
    b.setAttribute('aria-label', `${g.mode.kind[i]} compartment ${i + 1}`);
    b.dataset.cell = String(i);
    board.appendChild(b);
    cellBtns.push(b);
  }

  const renderer = createRenderer(canvas);
  let rects: Rect[] = [];
  let selected = -1;
  let preview: number[] | null = null;
  let surge = -1;
  let shake = 0;
  let countdown = opts.counted || opts.tutorial ? 0 : 3.999;
  let paused = false;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // tutorial state
  let step = opts.tutorial ? 0 : -1;
  const tutBox = el('div', 'tut');
  const tutSkip = el('button', 'tut-skip', 'Skip') as HTMLButtonElement;
  if (opts.tutorial) {
    // The prompt lives in the BAR and the skip lives in the HUD. Floating them over the board put
    // the instruction on top of the exact compartments it was telling the player to touch.
    bar.root.insertBefore(tutBox, bar.root.firstChild);
    hud.root.appendChild(tutSkip);
    tutSkip.addEventListener('click', () => {
      store.set('tutSeen', true);
      endPlay();
      showMenu();
    });
    g.gate = (a) => allowedIn(step, a);
    openStep(0);
  }

  function openStep(i: number): void {
    step = i;
    const s = STEPS[i];
    if (!s) return;
    s.setup?.(g!);
    tutBox.innerHTML = `<span class="step">Step ${i + 1} of ${STEPS.length}</span>${esc(s.copy)}`;
  }

  /**
   * TWO COORDINATE SPACES, ON PURPOSE, because they are genuinely different boxes.
   *
   * `rects` are DRAW space — relative to the canvas, which spans the padded wrapper. Pointer
   * coordinates are BOARD space — relative to the grid itself, because that is the origin
   * `cellAt`'s `floor(x / pitch)` model assumes. Measuring both against the same rect painted the
   * whole ship ten pixels left of the compartments you could actually tap: every fill, the cable and
   * every particle offset from its own hit target, and the rightmost column clipped off the screen.
   */
  function measure(): void {
    const wr = wrap.getBoundingClientRect();
    const br = board.getBoundingClientRect();
    if (br.width <= 0 || br.height <= 0) return; // transient 0-size: ignore, retry next frame
    renderer.resize(wr.width, wr.height);
    rects = cellBtns.map((b) => {
      const q = b.getBoundingClientRect();
      return { x: q.left - wr.left, y: q.top - wr.top, w: q.width, h: q.height };
    });
  }

  function geom(): Geom | null {
    if (rects.length < 2) return null;
    const a = rects[0];
    const b = rects[1];
    return { cols: g!.mode.cols, rows: g!.mode.rows, cellW: a.w, cellH: a.h, gap: Math.max(0, b.x - a.x - a.w) };
  }

  // ── input: drag routes, tap selects. Nothing else lives on this surface. ──
  let dragId: number | null = null;
  let samples: Sample[] = [];
  let downAt = { x: 0, y: 0 };
  let dragging = false;

  board.addEventListener('pointerdown', (e) => {
    if (paused || countdown > 0 || g.over) return;
    const r = board.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const cell = geom() ? cellAt(geom() as Geom, x, y) : -1;
    if (cell < 0) return;
    dragId = e.pointerId;
    downAt = { x, y };
    dragging = false;
    samples = [{ x, y }];
    board.setPointerCapture(e.pointerId);
    // A drag may only START on the reactor or on the cable's current end. Anywhere else is a tap.
    const end = g.spine[g.spine.length - 1];
    if (cell === g.mode.reactor || cell === end) {
      preview = null;
    }
  });

  board.addEventListener('pointermove', (e) => {
    if (dragId !== e.pointerId || paused) return;
    const r = board.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (!dragging && Math.hypot(x - downAt.x, y - downAt.y) < 8) return;
    const startCell = geom() ? cellAt(geom() as Geom, downAt.x, downAt.y) : -1;
    const end = g.spine[g.spine.length - 1];
    if (!dragging) {
      if (startCell !== g.mode.reactor && startCell !== end) {
        dragId = null; // a drag that began on some other compartment is not a route
        return;
      }
      dragging = true;
    }
    samples.push({ x, y });
    const gm = geom();
    if (!gm) return;
    // Appending from the cable's END keeps the existing route and adds to it — the cheap move.
    const base = startCell === g.mode.reactor ? [] : g.spine.slice(0, -1);
    const walked = pathFromPointerSamples(samples, gm, startCell, (c) =>
      step >= 0 ? allowedIn(step, { k: 0, p: [c], tk: 0, sq: 0 }) : true,
    );
    const next = [...base, ...walked];
    if (preview && preview.length !== next.length) sfx.play(CUE.snap);
    preview = next;
  });

  const endDrag = (e: PointerEvent, commit: boolean): void => {
    if (dragId !== e.pointerId) return;
    dragId = null;
    const p = preview;
    const wasDragging = dragging;
    dragging = false;
    preview = null;
    samples = [];
    if (!commit) return; // pointercancel: abort, commit nothing
    const r = board.getBoundingClientRect();
    const gm = geom();
    if (!wasDragging) {
      const cell = gm ? cellAt(gm, e.clientX - r.left, e.clientY - r.top) : -1;
      if (cell >= 0) {
        selected = cell;
        sfx.play(CUE.tap);
        paintBar();
      }
      return;
    }
    if (p && p.length > 1) {
      // Read the route BEFORE clearing the drag flag — clearing first is how a whole factory's worth
      // of slingshot games silently dropped every shot.
      if (session?.act({ k: 0, p: p.slice(1), tk: g.tick })) {
        surge = 0;
        sfx.play(CUE.commit);
      } else sfx.play(CUE.nope);
    }
  };
  board.addEventListener('pointerup', (e) => endDrag(e, true));
  board.addEventListener('pointercancel', (e) => endDrag(e, false));

  // ── verbs: hold buttons, never on the board ─────────────────────────────
  function buildBar() {
    const root = el('div', 'bar');
    const selinfo = el('div', 'selinfo', 'Tap a compartment to work on it');
    const verbs = el('div', 'verbs');
    const buttons: HTMLButtonElement[] = [];
    for (const k of [1, 2, 3] as const) {
      const b = el(
        'button',
        'verb',
        `<span class="lbl">${VERB_NAMES[k]}<span class="cost"></span></span><i class="hold"></i>`,
      ) as HTMLButtonElement;
      wireHold(b, k);
      verbs.appendChild(b);
      buttons.push(b);
    }
    const force = el(
      'button',
      'verb wide',
      `<span class="lbl">FORCE<span class="cost">+2 power, +3 heat/s</span></span><i class="hold"></i>`,
    ) as HTMLButtonElement;
    wireForce(force);
    root.appendChild(selinfo);
    root.appendChild(verbs);
    root.appendChild(force);
    return { root, selinfo, buttons, force };
  }

  function wireHold(b: HTMLButtonElement, k: 1 | 2 | 3): void {
    const dur = k === 1 ? T.SEAL_HOLD : k === 2 ? T.DOUSE_HOLD : T.VENT_HOLD;
    let raf = 0;
    let start = 0;
    let pressTick = 0;
    const fill = b.querySelector('.hold') as HTMLElement;
    const stop = (fire: boolean): void => {
      cancelAnimationFrame(raf);
      raf = 0;
      fill.style.width = '0%';
      if (!fire) return;
      // The load window is charged on the action's START, so the press tick rides along.
      const ok = session?.act({ k, c: selected, tk: pressTick });
      sfx.play(ok ? [CUE.seal, CUE.douse, CUE.vent][k - 1] : CUE.nope);
    };
    const tick = (): void => {
      const f = Math.min(1, (performance.now() - start) / (dur * 1000));
      fill.style.width = `${f * 100}%`;
      if (f >= 1) stop(true);
      else raf = requestAnimationFrame(tick);
    };
    b.addEventListener('pointerdown', (e) => {
      if (b.disabled || paused || countdown > 0) return;
      // While a pointer is captured on the grid the bar ignores new ids — a palm on the bar during a
      // drag must never fire a verb.
      if (dragId !== null) return;
      b.setPointerCapture(e.pointerId);
      start = performance.now();
      pressTick = g.tick;
      raf = requestAnimationFrame(tick);
    });
    const cancel = (): void => {
      if (raf) stop(false);
    };
    b.addEventListener('pointerup', cancel);
    b.addEventListener('pointercancel', cancel);
    b.addEventListener('pointerleave', cancel);
  }

  function wireForce(b: HTMLButtonElement): void {
    const fill = b.querySelector('.hold') as HTMLElement;
    const down = (e: PointerEvent): void => {
      if (paused || countdown > 0 || dragId !== null) return;
      b.setPointerCapture(e.pointerId);
      fill.style.width = '100%';
      session?.act({ k: 4, tk: g.tick });
    };
    const up = (): void => {
      fill.style.width = '0%';
      session?.act({ k: 5, tk: g.tick });
    };
    b.addEventListener('pointerdown', down);
    b.addEventListener('pointerup', up);
    b.addEventListener('pointercancel', up);
    b.addEventListener('pointerleave', up);
  }

  function buildHud() {
    const root = el('div', 'hud');
    const a = el('div', 'hud-row');
    const transit = el('div', 'meter transit grow', '<i></i>');
    const hull = el('div', 'meter hull grow', '<i></i>');
    const label = el('span', undefined, '');
    a.appendChild(label);
    a.appendChild(transit);
    a.appendChild(hull);
    const b = el('div', 'hud-row');
    const heat = el('div', 'heat', '<i></i>');
    const pips = el('div', 'pips grow');
    const dots = el('div', 'window-dots');
    const pause = el('button', 'x', '&#9208;') as HTMLButtonElement;
    pause.style.position = 'static';
    pause.style.width = '34px';
    pause.style.height = '28px';
    pause.setAttribute('aria-label', 'Pause');
    pause.addEventListener('click', () => togglePause());
    b.appendChild(heat);
    b.appendChild(pips);
    b.appendChild(dots);
    b.appendChild(pause);
    root.appendChild(a);
    root.appendChild(b);
    return { root, transit, hull, heat, pips, dots, label };
  }

  function paintHud(): void {
    const t = hud.transit.firstElementChild as HTMLElement;
    const u = hud.hull.firstElementChild as HTMLElement;
    t.style.width = `${g.mode.transitRate > 0 ? g.transit : Math.min(100, g.t / 3)}%`;
    u.style.width = `${Math.max(0, g.hull)}%`;
    hud.hull.classList.toggle('hurt', g.hull < 40);
    const h = hud.heat.firstElementChild as HTMLElement;
    h.style.width = `${Math.min(100, g.heat)}%`;
    hud.heat.classList.toggle('warm', g.heat >= 55 && g.heat < 80);
    hud.heat.classList.toggle('hot', g.heat >= 80);
    hud.label.textContent =
      g.mode.transitRate > 0 ? `${Math.round(g.transit)}%` : `${Math.round(g.t)}s`;

    let pips = '';
    for (let i = 0; i < g.output; i++) pips += `<b class="${i < spent() ? '' : 'off'}"></b>`;
    hud.pips.innerHTML = pips;
    let dots = '';
    const ord = g.ordinalAt(g.tick) - 1;
    for (let i = 0; i < T.TRIP_ORDINAL - 1; i++) dots += `<b class="${i < ord ? 'on' : ''}"></b>`;
    hud.dots.innerHTML = dots;
  }

  function spent(): number {
    let s = 0;
    for (let i = 0; i < g.poweredLen; i++) s += g.costOf(g.spine[i]);
    return s;
  }

  function paintBar(): void {
    const c = selected >= 0 ? g.cells[selected] : null;
    bar.selinfo.innerHTML = c
      ? `<b>${esc(g.mode.kind[selected].toUpperCase())}</b> · fire ${Math.round(c.fire)} · water ${Math.round(c.flood)} · breaches ${c.breaches}`
      : 'Tap a compartment to work on it';
    const legal = [c ? c.breaches > 0 : false, c ? c.fire > 0 : false, c ? c.fire > 0 : false];
    bar.buttons.forEach((b, i) => {
      b.disabled = !legal[i] || (step >= 0 && !STEPS[step]?.verbs.includes(i + 1));
      const cost = b.querySelector('.cost') as HTMLElement;
      cost.textContent = i === 2 ? `${T.VENT_HULL} hull` : `${[T.HEAT_SEAL, T.HEAT_DOUSE][i]} heat`;
    });
    bar.force.disabled = step >= 0 && !STEPS[step]?.verbs.includes(4);
  }

  function togglePause(): void {
    paused = !paused;
    if (!paused) return;
    openSheet(
      'Paused',
      `${HOW_TO_PLAY}<div class="row" style="margin-top:14px">
         <button class="btn ghost" data-act="menu">Quit to menu</button></div>`,
      () => {
        paused = false;
      },
    );
    const q = document.querySelector('[data-act="menu"]');
    q?.addEventListener('click', () => {
      document.querySelector('.overlay')?.remove();
      paused = false;
      endPlay();
      leaveToMenu();
    });
  }

  // ── keyboard: full parity, and the WCAG 2.1.1 route ─────────────────────
  const onKey = (e: KeyboardEvent): void => {
    if (paused || g.over) {
      if (e.key === 'Escape') togglePause();
      return;
    }
    const digits = '123456789';
    const letters = 'qwerty';
    let cell = -1;
    if (digits.includes(e.key)) cell = digits.indexOf(e.key);
    else if (letters.includes(e.key.toLowerCase())) cell = 9 + letters.indexOf(e.key.toLowerCase());
    if (cell >= 0 && cell < g.n) {
      selected = cell;
      paintBar();
      return;
    }
    const k = e.key.toLowerCase();
    if (k === 'arrowup' || k === 'arrowdown' || k === 'arrowleft' || k === 'arrowright') {
      const dx = k === 'arrowleft' ? -1 : k === 'arrowright' ? 1 : 0;
      const dy = k === 'arrowup' ? -1 : k === 'arrowdown' ? 1 : 0;
      const cur = selected < 0 ? g.mode.reactor : selected;
      const nx = Math.max(0, Math.min(g.mode.cols - 1, (cur % g.mode.cols) + dx));
      const ny = Math.max(0, Math.min(g.mode.rows - 1, ((cur / g.mode.cols) | 0) + dy));
      selected = ny * g.mode.cols + nx;
      paintBar();
      e.preventDefault();
      return;
    }
    if (k === 'a' && selected >= 0) {
      const next = [...g.spine.slice(1), selected];
      if (session?.act({ k: 0, p: next, tk: g.tick })) {
        surge = 0;
        sfx.play(CUE.commit);
      } else sfx.play(CUE.nope);
    } else if (k === 'z') {
      session?.act({ k: 0, p: g.spine.slice(1, -1), tk: g.tick });
    } else if (k === 'r') {
      session?.act({ k: 0, p: [], tk: g.tick });
    } else if (k === 's') session?.act({ k: 1, c: selected, tk: g.tick });
    else if (k === 'd') session?.act({ k: 2, c: selected, tk: g.tick });
    else if (k === 'v') session?.act({ k: 3, c: selected, tk: g.tick });
    else if (k === 'f') session?.act({ k: 4, tk: g.tick });
    else if (e.key === 'Escape') togglePause();
  };
  document.addEventListener('keydown', onKey);

  const onKeyUp = (e: KeyboardEvent): void => {
    if (e.key.toLowerCase() === 'f') session?.act({ k: 5, tk: g.tick });
  };
  document.addEventListener('keyup', onKeyUp);

  // ── the loop ─────────────────────────────────────────────────────────────
  // rAF renders. A setInterval pumps the simulation, because a backgrounded tab stops rAF entirely
  // and a host whose ship froze would strand the whole crew.
  let lastFrame = performance.now();
  let raf = 0;
  const frame = (now: number): void => {
    const dt = Math.min(0.1, (now - lastFrame) / 1000);
    lastFrame = now;
    if (!paused) {
      if (countdown > 0) {
        const was = Math.ceil(countdown);
        countdown -= dt;
        const is = Math.ceil(countdown);
        if (is !== was) sfx.play(is <= 0 ? CUE.go : CUE.beat);
      }
      surge = surge >= 0 && surge < 1 ? surge + dt * 3.2 : -1;
      shake = Math.max(0, shake - dt * 26);
      renderer.step(dt);
      drainEvents();
      if (step >= 0 && STEPS[step]?.done(g)) advanceStep();
    }
    renderer.draw(g, { rects, selected, surge, preview, shake, reduced });
    paintHud();
    drawCountdown();
    raf = requestAnimationFrame(frame);
  };

  const interval = window.setInterval(() => {
    if (paused || countdown > 0) return;
    session?.pump(performance.now());
  }, 16);

  const onResize = (): void => measure();
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  const countEl = el('div', 'count');
  countEl.hidden = true;
  wrap.appendChild(countEl);
  function drawCountdown(): void {
    if (countdown <= 0) {
      countEl.hidden = true;
      return;
    }
    countEl.hidden = false;
    const n = Math.ceil(countdown);
    countEl.textContent = n > 3 ? '' : n > 0 ? String(n) : 'GO';
  }

  function drainEvents(): void {
    const evs: Ev[] = session?.drainFx() ?? [];
    for (const e of evs) {
      const r = 'c' in e && typeof e.c === 'number' ? rects[e.c] : null;
      const cx = r ? r.x + r.w / 2 : 0;
      const cy = r ? r.y + r.h / 2 : 0;
      switch (e.k) {
        case 'breach':
          sfx.play(CUE.breach);
          shake = Math.max(shake, 6);
          renderer.burst(cx, cy, PALETTE.flood, 10, 90, reduced);
          break;
        case 'ignite':
          sfx.play(CUE.ignite);
          renderer.burst(cx, cy, PALETTE.fire, 8, 70, reduced);
          break;
        case 'destroy':
          sfx.play(CUE.destroy);
          shake = Math.max(shake, 10);
          renderer.burst(cx, cy, PALETTE.dead, 16, 130, reduced);
          break;
        case 'short':
          sfx.play(CUE.short);
          renderer.burst(cx, cy, PALETTE.wire, 10, 140, reduced);
          break;
        case 'seal':
          renderer.burst(cx, cy, PALETTE.good, 8, 80, reduced);
          break;
        case 'vent':
          renderer.burst(cx, cy, PALETTE.text, 12, 150, reduced);
          break;
        case 'bus_trip':
          sfx.play(CUE.trip);
          shake = Math.max(shake, 12);
          vibrate([40, 30, 60]);
          break;
        case 'scram':
          sfx.play(CUE.scram);
          shake = Math.max(shake, 14);
          vibrate([40, 60, 120]);
          break;
        default:
          break;
      }
    }
  }

  /**
   * The step is complete: show what it taught, then open the next one.
   *
   * `step` goes to -1 for the beat in between so `done()` cannot fire again on the frames while the
   * lesson is on screen — and so the gate refuses everything, which is what stops a player who is
   * still dragging from stumbling into the next step before they have read why the last one worked.
   */
  function advanceStep(): void {
    const done = STEPS[step];
    const next = step + 1;
    tutBox.innerHTML = `<span class="step">Step ${step + 1} of ${STEPS.length}</span>${esc(done.after)}`;
    sfx.play(CUE.commit);
    step = -1;
    paintBar();
    window.setTimeout(() => {
      if (next >= STEPS.length) {
        store.set('tutSeen', true);
        g.gate = null;
        endPlay();
        showMenu();
        return;
      }
      openStep(next);
      paintBar();
    }, 1800);
  }

  stopLoop = () => {
    cancelAnimationFrame(raf);
    window.clearInterval(interval);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('keyup', onKeyUp);
  };

  measure();
  // One deferred remeasure: a hidden tab reports clientWidth 0, and a board measured at 0 piles
  // every compartment into the corner. setTimeout, not rAF — rAF never fires in a background tab.
  window.setTimeout(measure, 60);
  paintBar();
  hudRefresh = paintHud;
  linkRefresh = setLink;
  raf = requestAnimationFrame(frame);

  // The synchronous test hook. Browser automation cannot draw an L — left_click_drag is two points
  // on a straight line, which never enters the corner cell of an L-shaped route — so the wiring is
  // proved through here and the algorithm is proved in tests/path.test.ts.
  (window as unknown as { __op: unknown }).__op = {
    injectPath: (p: number[]) => session?.act({ k: 0, p, tk: g.tick }),
    select: (c: number) => {
      selected = c;
      paintBar();
    },
    verb: (k: 1 | 2 | 3) => session?.act({ k, c: selected, tk: g.tick }),
    state: () => ({
      tick: g.tick,
      heat: g.heat,
      hull: g.hull,
      transit: g.transit,
      spine: g.spine.slice(),
      poweredLen: g.poweredLen,
      over: g.over,
      step,
    }),
    skipCountdown: () => {
      countdown = 0;
    },
  };

  function setLink(state: 'ok' | 'lost' | 'promoting'): void {
    hud.root.style.opacity = state === 'ok' ? '1' : '0.65';
    if (state !== 'ok') {
      bar.selinfo.innerHTML =
        state === 'lost'
          ? '<b>LINK LOST</b> — promoting in 1.8s'
          : '<b>You have the ship.</b> Keep going.';
    }
  }
  void opts.info;
}

function vibrate(pattern: number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported — the haptic is a bonus, never a requirement */
  }
}

function endPlay(): void {
  stopLoop?.();
  stopLoop = null;
  session?.destroy();
  session = null;
  setPlaying(false);
}

function leaveToMenu(): void {
  // rounds.finish() BEFORE navigating away from a live round: rematch.ts's vote() returns early
  // while a seated peer is still "playing", so a peer that leaves without it is excluded from every
  // subsequent round for the room's life.
  rounds?.finish();
  showMenu();
}

// ── results ─────────────────────────────────────────────────────────────────

function showResults(info?: RoundInfo<{ mode: string }>): void {
  const g = game;
  stopLoop?.();
  stopLoop = null;
  setPlaying(false);
  if (!g) return showMenu();

  rounds?.finish();
  const m = g.mode;
  const score = m.transitRate > 0 ? g.transit : g.t;
  matchTally = {
    runs: matchTally.runs + 1,
    wins: matchTally.wins + (g.win ? 1 : 0),
    best: Math.max(matchTally.best, score),
  };
  const best = Number(store.get(`best:${m.id}`, 0));
  if (score > best) store.set(`best:${m.id}`, score);

  const s = el('section', 'screen menu results');
  s.appendChild(el('h2', undefined, g.win ? 'You made it.' : 'The ship is lost.'));

  // THE SHARED OUTCOME LEADS. In a co-op summary the per-player breakdown is CONTRIBUTION, never a
  // ranking — a co-op results screen that quietly becomes a leaderboard rewards hogging the thing
  // that scores, which is the opposite of what the game is for.
  const cause = g.cause
    ? { flood: 'flooded out', destroyed: 'burned through', scram: 'the reactor gave out', vent: 'vented to nothing', drift: 'lost way and drifted' }[g.cause]
    : '';
  s.appendChild(
    el(
      'p',
      'verdict',
      m.transitRate > 0
        ? `<b>${Math.round(g.transit)}%</b> of the crossing in <b>${Math.round(g.t)}s</b>${g.win ? '.' : ` — ${esc(cause)}.`}`
        : `Held for <b>${Math.round(g.t)}s</b> — ${esc(cause)}.`,
    ),
  );

  const names = info?.players.map((p) => p.name) ?? ['You'];
  const crew = el('div', 'crew');
  g.seats.forEach((seat, i) => {
    crew.appendChild(
      el(
        'div',
        'crew-row',
        `<span class="who">${esc(names[i] ?? `Crew ${i + 1}`)}</span>` +
          `<span class="stat">${seat.actions} actions · ${seat.doused} doused · ${seat.sealed} sealed · ` +
          `${seat.cellSecs.toFixed(0)} cell-s</span>`,
      ),
    );
  });
  s.appendChild(crew);
  s.appendChild(
    el(
      'p',
      'verdict',
      `Best ${m.transitRate > 0 ? 'crossing' : 'hold'}: <b>${fmtScore(m, Math.max(best, score))}</b>` +
        (matchTally.runs > 1 ? ` · ${matchTally.wins}/${matchTally.runs} this session` : ''),
    ),
  );

  if (rounds) {
    const again = el('button', 'btn primary', 'Play again<span class="sub"></span>') as HTMLButtonElement;
    const sub = again.querySelector('.sub') as HTMLElement;
    again.addEventListener('click', () => {
      rounds?.vote();
      again.disabled = true;
      sub.textContent = 'waiting for the crew…';
    });
    const tick = window.setInterval(() => {
      const st = rounds?.state();
      if (!st) return window.clearInterval(tick);
      // A visible reason AND a horizon. Never unanimity-forever: one player still reading this must
      // not be able to hold the room hostage.
      if (st.startsInMs != null) sub.textContent = `starting in ${Math.ceil(st.startsInMs / 1000)}s`;
      else if (st.votes.length) sub.textContent = `${st.votes.length} of ${st.present.length} ready`;
    }, 250);
    s.appendChild(again);
    const toLobby = el('button', 'btn ghost', 'Back to the lobby') as HTMLButtonElement;
    toLobby.addEventListener('click', () => {
      window.clearInterval(tick);
      showLobby(getOrCreateRoomCode());
    });
    s.appendChild(toLobby);
  } else {
    const again = el('button', 'btn primary', 'Play again') as HTMLButtonElement;
    again.addEventListener('click', () => startSolo());
    s.appendChild(again);
  }

  const row = el('div', 'row');
  const menu = el('button', 'btn ghost', 'Menu') as HTMLButtonElement;
  menu.addEventListener('click', () => leaveToMenu());
  const fb = el('button', 'btn ghost results-feedback', 'Feedback') as HTMLButtonElement;
  fb.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  row.appendChild(menu);
  row.appendChild(fb);
  s.appendChild(row);

  sfx.play(g.win ? CUE.win : CUE.lose);
  mount(s);
}

// ── tutorial entry ──────────────────────────────────────────────────────────

function startTutorial(): void {
  // SOLO ONLY. In a live room the local Game is stomped by a snapshot every 83ms, so a completion
  // condition could never latch and the tutorial would be unfinishable — worse than not having one.
  if (net) {
    openSheet('How to play', HOW_TO_PLAY);
    return;
  }
  game = new Game({ seed: 20260826, mode: modeOf('patrol'), seats: 1, noHazards: true });
  session = createSession({ game, me: 0, ids: [null], onEnd: () => showMenu() });
  showPlay({ counted: true, tutorial: true });
}

// ── boot ────────────────────────────────────────────────────────────────────

window.addEventListener('beforeunload', () => {
  void net?.leave();
});

function boot(): void {
  const params = new URLSearchParams(location.search);
  const room = params.get('room');
  if (room && !deepLinkUsed) {
    // A room is honoured ONCE per page load, then cleared. Leave it in the URL and a reload drags
    // the player back into a room they left, with no way to start a new one.
    deepLinkUsed = true;
    void enterRoom(normalizeRoomCode(room), false);
    return;
  }
  if (!store.get('tutSeen', false)) {
    startTutorial();
    return;
  }
  showMenu();
}

boot();
